'use memo';

import {
  CampaignSearcherResult,
  AgxButton,
  AgxColumn,
  AgxDivider,
  AgxGlobalSearch,
  AgxRow,
  AgxTextInput,
  AgxTypography,
  Icon_CheckmarkOutline,
  Icon_EditOutline,
  SearchResultType,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  UpdateLegalNameVendorDetails,
  useGetUpdateLegalNameCampaignDetails,
  useInvalidateUseGetUpdateLegalNameCampaignDetails,
  useUpdateVendorLegalName,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { Fragment, useEffect, useMemo, useState } from 'react';
import SupportBackButton from '../SupportBackButton';

type DraftLegalNames = Record<string, string>;

const getVendorKey = (vendor: UpdateLegalNameVendorDetails) => vendor.vendorId ?? '';

const UpdateLegalName = () => {
  const { pushToast } = useToast();

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>();
  const [draftLegalNames, setDraftLegalNames] = useState<DraftLegalNames>({});
  const [editingVendorId, setEditingVendorId] = useState<string>();
  const [savingVendorId, setSavingVendorId] = useState<string>();

  const {
    data: campaignDetails,
    isPending: isLoadingCampaignDetails,
    isError: hasCampaignDetailsError,
  } = useGetUpdateLegalNameCampaignDetails(selectedCampaignId ?? '', !!selectedCampaignId);

  const invalidateUpdateLegalNameCampaignDetails =
    useInvalidateUseGetUpdateLegalNameCampaignDetails();

  const { updateVendorLegalName, updateVendorLegalNamePending } = useUpdateVendorLegalName({
    onSuccess: async (_returnData, inputData) => {
      if (inputData.campaignId) {
        await invalidateUpdateLegalNameCampaignDetails(inputData.campaignId);
      }

      setEditingVendorId(undefined);
      setSavingVendorId(undefined);
      pushToast({
        message: 'Legal name updated',
        type: 'success',
      });
    },
    onError: () => {
      setSavingVendorId(undefined);
      pushToast({
        message: 'Error updating legal name',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    const nextDrafts = (campaignDetails?.vendors ?? []).reduce<DraftLegalNames>(
      (drafts, vendor) => {
        const vendorKey = getVendorKey(vendor);
        if (!vendorKey) return drafts;

        return {
          ...drafts,
          [vendorKey]: vendor.legalName ?? '',
        };
      },
      {}
    );

    setDraftLegalNames(nextDrafts);
  }, [campaignDetails]);

  const hasSelectedCampaign = !!selectedCampaignId;
  const vendors = campaignDetails?.vendors ?? [];

  const onItemSelected = (itemType: SearchResultType, item: CampaignSearcherResult) => {
    if (
      itemType !== SearchResultType.ActiveCampaign &&
      itemType !== SearchResultType.ArchivedCampaign
    ) {
      return;
    }

    setSelectedCampaignId(item.campaignId);
    setEditingVendorId(undefined);
    setSavingVendorId(undefined);
    setDraftLegalNames({});
  };

  const selectedCampaignDetails = useMemo(() => {
    if (!campaignDetails) return [];

    return [
      { label: 'Property', value: campaignDetails.propertyAddress },
      { label: 'Agency', value: campaignDetails.agencyName },
      { label: 'Agent', value: campaignDetails.agentName },
    ].filter((detail) => !!detail.value);
  }, [campaignDetails]);

  const saveVendorLegalName = async (vendor: UpdateLegalNameVendorDetails) => {
    if (!campaignDetails?.campaignId || !vendor.vendorId) return;

    setSavingVendorId(vendor.vendorId);
    await updateVendorLegalName({
      campaignId: campaignDetails.campaignId,
      vendorId: vendor.vendorId,
      legalName: draftLegalNames[vendor.vendorId]?.trim() ?? '',
    });
  };

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel title="Update Legal Name">
        <AgxColumn gap="spacing-xl" fill>
          <AgxGlobalSearch
            searchForCampaigns
            searchForArchivedCampaigns
            onItemSelected={onItemSelected}
          />

          {isLoadingCampaignDetails && hasSelectedCampaign && <LoadingState />}

          {hasCampaignDetailsError && (
            <AgxTypography id="update-legal-name-error" type="body" size="small">
              Unable to load campaign details.
            </AgxTypography>
          )}

          {campaignDetails && (
            <AgxColumn gap="spacing-xl" fill>
              <AgxRow gap="spacing-xl" fill wrap>
                {selectedCampaignDetails.map((detail) => (
                  <AgxColumn key={detail.label} gap="spacing-xs">
                    <AgxTypography
                      id={`update-legal-name-${detail.label.toLowerCase()}-label`}
                      type="body_ui"
                      size="small"
                    >
                      {detail.label}
                    </AgxTypography>
                    <AgxTypography
                      id={`update-legal-name-${detail.label.toLowerCase()}-value`}
                      type="body"
                      size="medium"
                    >
                      {detail.value}
                    </AgxTypography>
                  </AgxColumn>
                ))}
              </AgxRow>

              <AgxColumn gap="spacing-lg" fill>
                {vendors.map((vendor, index) => {
                  const vendorKey = getVendorKey(vendor);
                  const isEditing = editingVendorId === vendorKey;
                  const isSaving = updateVendorLegalNamePending && savingVendorId === vendorKey;
                  const draftValue = draftLegalNames[vendorKey] ?? '';
                  const savedValue = vendor.legalName ?? '';
                  const hasChanged = draftValue.trim() !== savedValue.trim();

                  return (
                    <Fragment key={vendorKey}>
                      <AgxColumn gap="spacing-md" fill padding="spacing-lg">
                        <AgxRow
                          gap="spacing-md"
                          alignItems="alignCenter"
                          justifyContent="space-between"
                          fill
                          wrap
                        >
                          <AgxColumn gap="spacing-xs">
                            <AgxTypography
                              id={`update-legal-name-vendor-${vendorKey}-name`}
                              type="title"
                              size="small"
                            >
                              {vendor.displayName}
                            </AgxTypography>
                            <AgxTypography
                              id={`update-legal-name-vendor-${vendorKey}-type`}
                              type="body_ui"
                              size="small"
                            >
                              {vendor.vendorType}
                              {vendor.contactSummary ? ` | ${vendor.contactSummary}` : ''}
                            </AgxTypography>
                          </AgxColumn>

                          <AgxButton
                            id={`update-legal-name-vendor-${vendorKey}-action`}
                            text={isSaving ? 'Saving' : isEditing ? 'Save' : 'Edit'}
                            variant={isEditing ? 'primary' : 'hollow'}
                            size="small"
                            leftIcon={
                              isEditing ? (
                                <Icon_CheckmarkOutline width={16} height={16} />
                              ) : (
                                <Icon_EditOutline width={16} height={16} />
                              )
                            }
                            disabled={
                              isSaving ||
                              (isEditing && (!hasChanged || draftValue.trim().length === 0))
                            }
                            onClick={() => {
                              if (!isEditing) {
                                setEditingVendorId(vendorKey);
                                return;
                              }

                              void saveVendorLegalName(vendor);
                            }}
                          />
                        </AgxRow>

                        <AgxTextInput
                          id={`update-legal-name-vendor-${vendorKey}-legal-name`}
                          type="text"
                          label="Legal name"
                          defaultValue={draftValue}
                          readonly={!isEditing || isSaving}
                          disabled={isSaving}
                          stretch
                          hideOptionalText
                          onChange={(value) =>
                            setDraftLegalNames((previousDrafts) => ({
                              ...previousDrafts,
                              [vendorKey]: value,
                            }))
                          }
                        />
                      </AgxColumn>
                      {index < vendors.length - 1 && <AgxDivider variant="horizontal" />}
                    </Fragment>
                  );
                })}
              </AgxColumn>
            </AgxColumn>
          )}
        </AgxColumn>
      </Panel>
    </AgxColumn>
  );
};

export default UpdateLegalName;
